import { createContext, useContext, useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import { gql } from 'graphql-tag';
import { subDays, format } from 'date-fns';

// GraphQL query to get the count of unique target+domain combinations with unviewed ACL bypasses
// This counts by how the cards are grouped in the UI rather than individual bypass entries
const GET_ACL_BYPASS_COUNT = gql`
  query GetAclBypassCount($startDate: timestamp!, $endDate: timestamp!) {
    job_runs(
      where: {
        job_name: {_eq: "domain_header_bypass_size_match"},
        viewed_at: {_is_null: true},
        times: {_gte: $startDate, _lte: $endDate},
        result: {_is_null: false}
      },
      distinct_on: [target_id, live_domain]
    ) {
      target_id
      live_domain
    }
  }
`;

export const AclBypassCountContext = createContext(null);

export function AclBypassCountProvider({ children }) {
  const [count, setCount] = useState(0);
  
  // Memoize dates
  const startDate = useMemo(() => 
    format(subDays(new Date(), 30), 'yyyy-MM-dd'), 
  []);
  const endDate = useMemo(() => 
    format(new Date(), 'yyyy-MM-dd HH:mm:ss'), 
  []);

  // Updated query config
  const { data, loading, refetch } = useQuery(GET_ACL_BYPASS_COUNT, {
    variables: { startDate, endDate },
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    pollInterval: 600000,
    notifyOnNetworkStatusChange: false,
    onError: (error) => console.error('Error fetching ACL bypass count:', error)
  });

  // Optimized useEffect
  useEffect(() => {
    const newCount = data?.job_runs?.length || 0;
    setCount(prev => prev !== newCount ? newCount : prev);
  }, [data]);

  // Use useMemo to memoize the context value
  const contextValue = useMemo(() => ({ 
    count, 
    setCount, 
    loading, 
    refetch 
  }), [count, loading, refetch]);

  return (
    <AclBypassCountContext.Provider value={contextValue}>
      {children}
    </AclBypassCountContext.Provider>
  );
}

AclBypassCountProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useAclBypassCount() {
  const context = useContext(AclBypassCountContext);
  if (!context) {
    throw new Error('useAclBypassCount must be used within a AclBypassCountProvider');
  }
  return context;
} 